/**
 * OpenPanel product analytics (web SDK).
 *
 * One instance for the whole app, created lazily and only when two things
 * are true: a client id is configured (VITE_OPENPANEL_CLIENT_ID) and the
 * visitor has accepted analytics cookies (src/lib/analyticsConsent.ts).
 *
 * Why lazy rather than the SDK's own off switch: in @openpanel/sdk that
 * option means "queue until ready()", so events would pile up in memory for
 * a visitor who never consents. Constructing on consent means the SDK's own
 * first screen_view fires at the right moment, and the `filter` below drops
 * everything again if consent is later withdrawn.
 *
 * What the SDK does on its own once it exists:
 *   - screen_view on every history change (trackScreenViews)
 *   - link_out on clicks to other hosts (trackOutgoingLinks)
 *   - any button or anchor with data-track="event_name" becomes an event,
 *     with its other data-* attributes as properties (trackAttributes)
 *   - device and session ids: the API assigns them per browser, and the
 *     worker derives session_start / session_end (30 minutes idle) server
 *     side. Nothing here manages sessions.
 *
 * Identity: identifyAnalyticsUser() ties the device to a profile id with
 * coarse traits; setAnalyticsMarket() puts the profile in a group per
 * market (country), which is how every report can be sliced by market.
 * useAnalyticsIdentity (src/hooks) is the one caller of both.
 *
 * Every call here is fire-and-forget and swallows errors: analytics must
 * never break a booking.
 */
import { OpenPanel } from '@openpanel/web';
import { hasAnalyticsConsent } from '@/lib/analyticsConsent';
import { APP_ENV, openPanelApiUrl, openPanelClientId } from '@/lib/env';
import { logger } from '@/lib/logger';
import { scrubAnalyticsEvent } from '@/lib/privacyScrub';

let instance: OpenPanel | null = null;
let listening = false;

/** True when the SDK is configured for this build (env), regardless of consent. */
export function isOpenPanelConfigured(): boolean {
  return openPanelClientId.length > 0;
}

function detectPlatform(): 'web' | 'android' | 'ios' {
  // Capacitor sets window.Capacitor in native builds; the web bundle is the
  // same code, so this is the only thing that tells the two apart. Read it
  // reflectively rather than by widening Window: the repo's cast ratchet
  // counts double casts through unknown, and @capacitor/core is not
  // imported anywhere in src yet.
  const cap = Reflect.get(globalThis, 'Capacitor') as { getPlatform?: () => string } | undefined;
  const p = cap?.getPlatform?.();
  if (p === 'android' || p === 'ios') return p;
  return 'web';
}

function create(): OpenPanel {
  const op = new OpenPanel({
    clientId: openPanelClientId,
    apiUrl: openPanelApiUrl || undefined,
    trackScreenViews: true,
    trackOutgoingLinks: true,
    trackAttributes: true,
    // Consent is re-checked per event so withdrawing it stops tracking at once.
    // Every event passes here before it is sent: page URLs (__path,
    // __referrer) and link_out hrefs lose their query and fragment (M11) —
    // the SDK records window.location.href, which carries auth codes and ids.
    filter: (event) => hasAnalyticsConsent() && scrubAnalyticsEvent(event),
    debug: import.meta.env.DEV,
  });
  op.setGlobalProperties({
    app_version: import.meta.env.VITE_BUILD_SHA || 'dev',
    environment: APP_ENV,
    platform: detectPlatform(),
  });
  return op;
}

/**
 * The shared instance, or null when analytics cannot run right now
 * (not configured, no consent yet, or not in a browser).
 */
export function getOpenPanel(): OpenPanel | null {
  if (typeof window === 'undefined') return null;
  if (!isOpenPanelConfigured()) return null;
  if (!instance) {
    if (!hasAnalyticsConsent()) return null;
    try {
      instance = create();
    } catch (error) {
      logger.warn('OpenPanel init failed', error);
      return null;
    }
  }
  return instance;
}

/**
 * Start tracking as soon as consent is granted, even if nothing has asked
 * for the instance yet. Safe to call more than once.
 */
export function initOpenPanel(): void {
  if (typeof window === 'undefined' || listening || !isOpenPanelConfigured()) return;
  listening = true;
  window.addEventListener('cookieConsentChanged', (event) => {
    const granted = (event as CustomEvent<{ analytics?: boolean }>).detail?.analytics === true;
    if (granted) getOpenPanel();
  });
  getOpenPanel();
}

export function trackAnalyticsEvent(name: string, properties?: Record<string, unknown>): void {
  try {
    getOpenPanel()?.track(name, properties);
  } catch (error) {
    logger.warn('OpenPanel track failed', error);
  }
}

export function trackAnalyticsScreen(path: string, properties?: Record<string, unknown>): void {
  try {
    getOpenPanel()?.screenView(path, properties);
  } catch (error) {
    logger.warn('OpenPanel screenView failed', error);
  }
}

/**
 * Tie the device's events to a person. Only the profile id and coarse,
 * non-identifying traits are sent (role, status, market); never name,
 * email or phone.
 */
export function identifyAnalyticsUser(
  profileId: string,
  properties?: Record<string, unknown>,
): void {
  try {
    getOpenPanel()?.identify({ profileId, properties });
  } catch (error) {
    logger.warn('OpenPanel identify failed', error);
  }
}

/**
 * Put the current profile in its market group (one group per operating
 * country, id `market:<ISO code>`), so OpenPanel's group filter can split
 * every chart by market. Upsert is idempotent; setGroup also stamps the
 * group on every later event from this device.
 */
export function setAnalyticsMarket(countryCode: string, countryName?: string): void {
  const code = countryCode.trim().toUpperCase();
  if (!code) return;
  const op = getOpenPanel();
  if (!op) return;
  const id = `market:${code}`;
  try {
    void op.upsertGroup({ id, type: 'market', name: countryName?.trim() || code });
    void op.setGroup(id);
  } catch (error) {
    logger.warn('OpenPanel group failed', error);
  }
}

/** Forget the person on sign-out so the next login on this device starts clean. */
export function clearAnalyticsUser(): void {
  try {
    instance?.clear();
  } catch (error) {
    logger.warn('OpenPanel clear failed', error);
  }
}
